import { isAxiosError } from "axios";

/**
 * The server answered, and the answer is "there is no such thing".
 *
 * Lives here rather than next to the query that first needed it because callers
 * of both kinds now depend on it — a query config and a pure state resolver —
 * and this module is a leaf: importing it does not drag in `axiosInstance`, the
 * auth interceptors and `@solidjs/router` behind them, which is what a node
 * unit test of a resolver cannot afford.
 */
export const isNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

/**
 * The server answered, and the answer is "you may not". Sibling of `isNotFoundError`,
 * kept here for the same reason: this module is a leaf, so a node unit test can import
 * it without dragging in `axiosInstance` and the router behind it.
 */
export const isForbiddenError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 403;

/**
 * The server answered "you have asked too often". Sibling of `isNotFoundError`, kept
 * here for the same reason.
 *
 * Distinct from `isClientError` on purpose: a 429 is the one 4xx a caller may want to
 * treat as temporary. Query configs use it to switch retries **off** — each retry spends
 * another slot of the same bucket, so retrying pushes the recovery further away instead
 * of closer.
 */
export const isTooManyRequestsError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 429;

/**
 * Il backend ha risposto «quel nome è di qualcun altro» (409
 * `USERNAME_TAKEN`). Il `code` è obbligatorio: `PATCH /profile` risponde 409
 * anche per il conflitto di versione del lock ottimistico, e leggerlo come
 * «già in uso» direbbe all'utente una bugia su un nome libero.
 */
export const isUsernameTakenError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 409 &&
  (error.response.data as { code?: unknown } | undefined)?.code ===
    "USERNAME_TAKEN";

/**
 * I secondi di attesa dichiarati da un header di rate limit, nelle tre forme che un
 * server può usare: un numero, una stringa numerica, o una data HTTP (`Retry-After`
 * ammette entrambe le ultime due).
 *
 * Pura e senza `isAxiosError`: prende il **valore**, non l'errore, così chi la chiama
 * decide da quale header leggerlo. Vive qui e non accanto al primo chiamante perché ora
 * i chiamanti sono due — la ricerca semantica e la dashboard freelance — e due copie di
 * questa aritmetica divergerebbero al primo bordo (`0`, una data passata, `"1.2"`).
 */
export const retryAfterSeconds = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.ceil(value));
  }
  if (typeof value !== "string" || value.trim() === "") return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));

  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.ceil((date - Date.now()) / 1000));
};

/**
 * The server answered with a 4xx: the request itself is wrong (wrong scope, wrong id,
 * wrong params) and repeating it verbatim cannot change the answer. Query configs use
 * it to switch retries off, so a `403` does not keep a section "loading" for the three
 * attempts TanStack retries by default.
 *
 * A rejection with no `response` (network error, timeout, aborted) is deliberately NOT
 * a client error: those are exactly the ones worth retrying.
 */
export const isClientError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status !== undefined && status >= 400 && status < 500;
};

/**
 * The server answered "your plan does not include this".
 *
 * Matched on the error *code*, never on the bare 403: a plain forbidden can
 * mean a missing permission or a foreign resource, and turning those into an
 * upsell would hide real bugs behind a sales modal — the worst possible way to
 * discover a fault. Only this exact contract opens the paywall.
 */
export const isPlanFeatureUnavailableError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 403 &&
  (error.response?.data as { error?: string } | undefined)?.error ===
    "plan_feature_unavailable";

export type ZodValidationError<TFieldKey extends string = string> = {
  error: {
    fieldErrors: Partial<Record<TFieldKey, string[]>>;
    formErrors: string[];
  };
};

export type ApiFormError<TFieldKey extends string = string> = {
  fieldErrors: Partial<Record<TFieldKey, string[]>> | null;
  error: string | null;
};

/**
 * Estrae fieldErrors e messaggio errore da un errore API (Zod validation o generico).
 * Utile per popolare lo stato di form dopo submit fallito.
 */
export function extractApiFormError<TFieldKey extends string = string>(
  error: unknown,
): ApiFormError<TFieldKey> {
  if (typeof error === "object" && error !== null && "response" in error) {
    const data = (error as { response?: { data?: unknown } }).response?.data;
    if (
      data &&
      typeof data === "object" &&
      ("error" in data || "errors" in data)
    ) {
      const zodPayload =
        (data as { error?: ZodValidationError<TFieldKey>["error"] }).error ??
        (data as { errors?: ZodValidationError<TFieldKey>["error"] }).errors;
      if (!zodPayload) {
        return { fieldErrors: null, error: null };
      }
      const { fieldErrors, formErrors } = zodPayload;
      const hasFieldErrors =
        fieldErrors &&
        typeof fieldErrors === "object" &&
        Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors: hasFieldErrors ? fieldErrors : null,
        error: formErrors?.length ? (formErrors[0] ?? null) : null,
      };
    }
    const msg =
      (data as { msg?: string })?.msg ??
      (data as { message?: string })?.message;
    if (typeof msg === "string" && msg.length > 0) {
      return { fieldErrors: null, error: msg };
    }
  }
  if (error instanceof Error && error.message) {
    return { fieldErrors: null, error: error.message };
  }
  return { fieldErrors: null, error: null };
}

/**
 * Best actionable message for an API error, or `fallback` when the response carries
 * nothing usable.
 *
 * Prefers the first field-level message, because Zod `flatten()` payloads that fail a
 * single field put the real text under `fieldErrors` and leave `formErrors` empty — so
 * reading only the form-level string would silently degrade to the generic fallback.
 */
export function resolveApiErrorMessage<TFallback>(
  error: unknown,
  fallback: TFallback,
): string | TFallback {
  const { fieldErrors, error: formError } = extractApiFormError(error);
  const firstFieldMessage = Object.values(fieldErrors ?? {})
    .flatMap((messages) => messages ?? [])
    .find((message) => typeof message === "string" && message.length > 0);

  return firstFieldMessage ?? formError ?? fallback;
}

/**
 * Messaggio d'errore preso SOLO dal body della risposta BE (`msg`/`message`),
 * mai da `error.message`: per gli errori axios quello è testo tecnico in
 * inglese ("timeout of 30000ms exceeded", "Network Error") che non deve
 * raggiungere l'utente. Ritorna stringa vuota quando il body non porta nulla.
 */
export function getApiResponseBodyMessage(error: unknown): string {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return "";
  }
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (typeof data !== "object" || data === null) return "";
  const msg =
    (data as { msg?: unknown }).msg ?? (data as { message?: unknown }).message;
  return typeof msg === "string" ? msg : "";
}

type EffectErrorData = Record<string, unknown> & { _tag: string };

export type TranslateFn = (
  key: string,
  params?: Record<string, string | number>,
) => unknown;

type EffectErrorDef = {
  key: string;
  resolve: (data: EffectErrorData) => {
    params?: Record<string, string | number>;
    fallback: string;
  };
};

const EFFECT_ERRORS: Record<string, EffectErrorDef> = {
  DbError: {
    key: "errors.api.db",
    resolve: () => ({
      fallback: "Si è verificato un errore. Riprova più tardi.",
    }),
  },
  CompanyNotFoundError: {
    key: "errors.api.companyNotFound",
    resolve: () => ({ fallback: "Azienda non trovata" }),
  },
  JobOfferNotFoundError: {
    key: "errors.api.jobOfferNotFound",
    resolve: () => ({ fallback: "Offerta di lavoro non trovata" }),
  },
  JobApplicationNotFoundError: {
    key: "errors.api.jobApplicationNotFound",
    resolve: () => ({ fallback: "Candidatura non trovata" }),
  },
  ProfileNotFoundError: {
    key: "errors.api.profileNotFound",
    resolve: () => ({ fallback: "Profilo non trovato" }),
  },
  InvalidParameterError: {
    key: "errors.api.invalidParameter",
    resolve: (d) => ({
      params: { parameter: String(d.parameter) },
      fallback: `Parametro non valido: ${String(d.parameter)}`,
    }),
  },
  MissingRejectionReasonsError: {
    key: "errors.api.missingRejectionReasons",
    resolve: () => ({ fallback: "Seleziona almeno un motivo di rifiuto" }),
  },
  InvalidRejectionReasonError: {
    key: "errors.api.invalidRejectionReason",
    resolve: (d) => ({
      params: { reason: String(d.reason) },
      fallback: `Motivo di rifiuto non valido: ${String(d.reason)}`,
    }),
  },
  RejectionMessageTooLongError: {
    key: "errors.api.rejectionMessageTooLong",
    resolve: (d) => ({
      params: { length: String(d.length) },
      fallback: `Il messaggio supera il limite massimo di caratteri (${String(d.length)})`,
    }),
  },
  InvalidStatusError: {
    key: "errors.api.invalidStatus",
    resolve: (d) => ({
      params: { status: String(d.status) },
      fallback: `Stato non valido: ${String(d.status)}`,
    }),
  },
  EmailSendError: {
    key: "errors.api.emailSend",
    resolve: () => ({
      fallback: "Errore durante l'invio dell'email al candidato",
    }),
  },
};

/**
 * Estrae il messaggio di errore da una risposta API o da un Error.
 * Gestisce sia gli errori classici (msg/message) sia gli errori tagged di Effect.js (_tag).
 * Quando viene fornita una funzione `t`, risolve i messaggi tramite i18n.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  t?: TranslateFn,
): string {
  const apiError = error as {
    response?: {
      data?: { msg?: string; message?: string } & Partial<EffectErrorData>;
    };
    message?: string;
  };

  const responseData = apiError?.response?.data;

  if (
    responseData &&
    typeof responseData === "object" &&
    typeof responseData._tag === "string"
  ) {
    const errorDef = EFFECT_ERRORS[responseData._tag];
    if (errorDef) {
      const { params, fallback: itFallback } = errorDef.resolve(
        responseData as EffectErrorData,
      );
      return t ? String(t(errorDef.key, params)) : itFallback;
    }
  }

  const rawError = (responseData as { error?: unknown })?.error;
  const msg =
    responseData?.msg ??
    responseData?.message ??
    (typeof rawError === "string" ? rawError : undefined);
  if (typeof msg === "string" && msg.length > 0) {
    return msg;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

const firstNonEmptyMessage = (messages: unknown): string | null => {
  if (!Array.isArray(messages)) return null;
  return (
    messages.find(
      (message): message is string =>
        typeof message === "string" && message.length > 0,
    ) ?? null
  );
};

/**
 * The first message the backend wrote for the user in a `400 { error: flatten() }`
 * body, or `null` for every other error.
 *
 * Why it exists next to the two helpers above: `getApiErrorMessage` never reads
 * `flatten()`, so on a validation 400 it returns axios' English `error.message`;
 * `resolveApiErrorMessage` does read `flatten()`, but on a network error or a 5xx it
 * returns that same `error.message`. Neither alone keeps both, so the call sites do
 * `getApiValidationMessage(error) ?? <today's message>`. Changing `getApiErrorMessage`
 * itself would touch 41 calls in 27 files, which is why this stays a separate helper.
 *
 * Only the `response` is handed to `extractApiFormError`, never the error, so axios'
 * `error.message` can never come out of here. A body that is not the flatten shape
 * (`{ msg }`, `{ _tag }`, a plain string) gives `null`, and so does any non-400 status.
 */
export function getApiValidationMessage(error: unknown): string | null {
  const response =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { status?: unknown; data?: unknown } }).response
      : undefined;
  if (response?.status !== 400) return null;

  const data = response.data;
  if (typeof data !== "object" || data === null || !("error" in data)) {
    return null;
  }
  const flatten = data.error;
  if (
    typeof flatten !== "object" ||
    flatten === null ||
    !("formErrors" in flatten || "fieldErrors" in flatten)
  ) {
    return null;
  }

  const { fieldErrors } = extractApiFormError({ response });
  const fieldMessage = Object.values(fieldErrors ?? {})
    .map(firstNonEmptyMessage)
    .find((message) => message !== null);

  return (
    fieldMessage ??
    firstNonEmptyMessage(
      "formErrors" in flatten ? flatten.formErrors : undefined,
    )
  );
}
